<template>
  <div class="filters-dropdown">
    <b-dropdown
      variant="outline-primary"
      no-caret
      right
      @hide="onDropdownHide"
    >
      <template #button-content>
        <feather-icon
          v-b-tooltip.hover="'Filters'"
          icon="FilterIcon"
        />
        <b-badge
          v-b-tooltip.hover="'Applied filters'"
          variant="success"
          class="filter-count"
        >
          {{ totalActiveFilters }}
        </b-badge>
      </template>

      <b-dropdown-item
        v-for="([key, filter]) in Object.entries(getFilters)"
        :key="key"
        @click.stop="preventClose = true"
      >
        <div v-if="checkFilterVisibility(key)">
          <!-- Filters with tristate input -->
          <div
            v-if="filter.inputType === 'tristate'"
            class="online-status-wrapper"
            @click.stop="preventClose = true"
            @mousedown.stop="preventClose = true"
          >
            <label class="online-status-label">{{ $t(filter.label) }}</label>
            <tristate
              v-model="selectedFilters[key]"
              :tooltip-yes="$t(filter.tooltipYes)"
              :tooltip-no="$t(filter.tooltipNo)"
              :tooltip-neutral="$t(filter.tooltipNeutral)"
              @change="selectedFilters[key] = $event"
            />
          </div>

          <b-dropdown
            v-if="filter.inputType === 'assignees-selector'"
            :ref="`${key}Dropdown`"
            class="sub-dropdown"
            variant="link"
            no-caret
            :dropleft="!IS_MOBILE()"
            @show="preventClose = true"
            @hide="onDropdownHide"
          >
            <template #button-content>
              <feather-icon
                icon="ChevronLeftIcon"
                class="mr-1"
              />
              <span>{{ $t(filter.label) }}</span>
              <b-badge
                v-b-tooltip.hover="`Selected ${key}`"
                class="filter-count ml-1 mb-0"
                variant="success"
              >
                {{ filter.multiple ? selectedFilters[key]?.length : selectedFilters[key] ? 1 : 0 }}
              </b-badge>
            </template>
            <b-dropdown-header>
              <h6>{{ $t(filter.header || `Select ${key}`) }}</h6>

              <b-dropdown-item
                style="width: 240px;"
                @click.stop="preventClose = true"
              >
                <div
                  @click.stop="preventClose = true"
                  @mousedown.stop="preventClose = true"
                >
                  <assignee-selector
                    :multiple="true"
                    :members="filter.data"
                    :clearable="true"
                    @input="selectedFilters[key] = $event ? [...$event] : null"
                  />
                </div>
              </b-dropdown-item>
            </b-dropdown-header>
          </b-dropdown>

          <!-- Filters with select input -->
          <b-dropdown
            v-if="filter.inputType === 'select'"
            :ref="`${key}Dropdown`"
            class="sub-dropdown"
            variant="link"
            no-caret
            :dropleft="!IS_MOBILE()"
            @show="preventClose = true"
            @hide="onDropdownHide"
          >
            <template #button-content>
              <feather-icon
                icon="ChevronLeftIcon"
                class="mr-1"
              />
              <span>{{ $t(filter.label) }}</span>
              <b-badge
                v-b-tooltip.hover="`Selected ${key}`"
                class="filter-count ml-1 mb-0"
                variant="success"
              >
                {{ filter.multiple ? selectedFilters[key]?.length : selectedFilters[key] ? 1 : 0 }}
              </b-badge>
            </template>
            <b-dropdown-header>
              <h6>{{ $t(filter.header || `Select ${key}`) }}</h6>

              <b-dropdown-item
                style="width: 240px;"
                @click.stop="preventClose = true"
              >
                <div
                  @click.stop="preventClose = true"
                  @mousedown.stop="preventClose = true"
                >
                  <vue-select
                    v-model="selectedFilters[key]"
                    :options="filter.data"
                    :multiple="filter.multiple || false"
                    :clearable="true"
                    :close-on-select="!filter.multiple"
                    :searchable="true"
                    :taggable="filter.taggable || false"
                    :placeholder="$t(`Select ${filter.text}`)"
                    :reduce="r => filter.reduce ? r[filter.reduce] : r"
                    :label="filter.optionLabel || 'label'"
                    @mousedown.native.stop="preventClose = true"
                    @search:focus="preventClose = true"
                    @search:blur="preventClose = true"
                  >
                    <template #selected-option="option">
                      {{ option[filter.selectedOptionLabel || 'label'] }}
                    </template>

                    <template #option="option">
                      <div class="d-flex align-items-center">
                        <span v-if="filter.optionIcon">
                          <feather-icon
                            :icon="filter.optionIcon"
                            :class="`text-${option.color || 'secondary'} mr-1`"
                          />
                        </span>
                        <span>{{ option[filter.optionLabel || 'label'] }}</span>
                      </div>
                    </template>

                    <template
                      v-if="filter.taggable"
                      #no-options="{ search }"
                    >
                      <span v-if="search">Press Enter to add "{{ search }}"</span>
                      <span v-else>{{ $t('Type and press Enter') }}</span>
                    </template>
                  </vue-select>
                </div>
              </b-dropdown-item>
            </b-dropdown-header>
          </b-dropdown>
        </div>
      </b-dropdown-item>

      <b-dropdown-divider />

      <b-dropdown-item-button
        variant="success"
        @click="onChange"
      >
        {{ $t('Apply Filters') }}
      </b-dropdown-item-button>
    </b-dropdown>
  </div>
</template>

<script setup>
/* eslint-disable no-unused-vars, no-undef */
import useApollo from '@/plugins/graphql/useApollo'
import {
  BDropdown, BDropdownHeader, BDropdownDivider, BDropdownItem, BDropdownItemButton, BBadge, VBTooltip,
} from 'bootstrap-vue'
import { ref, onMounted, watch } from 'vue'
import VueSelect from 'vue-select'
import store from '@/store'
import Tristate from '@/views/common/components/Tristate.vue'
import AssigneeSelector from '@/views/ProjectManagement/tasks/AssigneeSelector.vue'
import useFilters from './useFilters'

const {
  getFilters, setFilters, getSelectedFilters, setActiveFilters, totalActiveFilters, clearActiveFilters,
} = useFilters()
// when true, the next dropdown hide will be prevented (keeps menu open)
const preventClose = ref(false)

const props = defineProps({
  excludeFilters: {
    type: Array,
    default: () => [],
  },

  includeFilters: {
    type: Array,
    default: () => [],
  },
})

defineExpose({
  directives: {
    'b-tooltip': VBTooltip,
  },
})

const selectedFilters = ref({})

const getProjectTags = () => {
  useApollo.project.getProjectTags({
    projectId: store.state.project.selectedProject,
    first: -1,
  }).then(response => {
    setFilters('tags', response.data.project.tags.data)
  })
}

const onDropdownHide = bvEvent => {
  // bvEvent is BootstrapVue's event object and supports preventDefault()
  if (preventClose.value) {
    bvEvent.preventDefault()
    // reset flag so subsequent hides behave normally
    preventClose.value = false
  }
}

const checkFilterVisibility = key => (props.excludeFilters.length && !props.excludeFilters.includes(key)) || (props.includeFilters.length && props.includeFilters.includes(key)) || (!props.excludeFilters.length && !props.includeFilters.length)

const onChange = () => {
  const tristateMapping = {
    yes: true,
    no: false,
    neutral: null,
  }

  const filters = Object.entries(selectedFilters.value).reduce((acc, [key, value]) => {
    if (Array.isArray(value)) {
      const filter = getFilters.value[key]
      if (filter.taggable) {
        acc[key] = value
      } else {
        acc[key] = [...filter.data
          .filter(item => value.includes(item[filter.reduce])),
        ]
      }
    } else if (getFilters.value[key].inputType === 'tristate') {
      acc[key] = tristateMapping[value]
    } else {
      acc[key] = value
    }

    return acc
  }, {})

  setActiveFilters({ ...filters })
  preventClose.value = false
}

watch(() => getSelectedFilters.value, newVal => {
  selectedFilters.value = { ...newVal }
}, { immediate: true, deep: true })

onMounted(() => {
  clearActiveFilters()
  if (checkFilterVisibility('tags')) {
    getProjectTags()
  }
})
</script>

<style lang="scss">
.filters-dropdown {
  .sub-dropdown {
    padding: 0;

    .dropdown-toggle {
      padding: 8px;
    }
  }

  .dropdown-toggle {
    padding: 7px 21px;
  }

  .filter-count {
    margin-bottom: 4px;
    width: 18px;
    height: 18px;
    padding: 4px;
  }

  .dropdown-item {
    padding: 0;
  }

  button.dropdown-item {
    width: 100%;
    text-align: center;
    font-weight: bold;
    padding: 6px;
  }

  .dropdown-item:active,
  .dropdown-item:hover,
  button.dropdown-item:active,
  button.dropdown-item:hover {
    background-color: transparent !important;
  }

  .vs__deselect {
    fill: #999;
  }

  .tag-select-wrapper {
    padding: 12px;
    background-color: transparent !important;

    &:active,
    &:hover {
      background-color: transparent !important;
    }

    .tag-select-label {
      display: block;
      margin-bottom: 8px;
      font-weight: 500;
      font-size: 0.875rem;
      color: var(--bs-body-color);
      pointer-events: none;
      user-select: none;
      background-color: transparent !important;

      &:active,
      &:hover {
        background-color: transparent !important;
      }
    }

    .vs__control {
      padding: 0 4px;

      &:active,
      &:hover {
        background-color: transparent !important;
      }
    }
  }

  // Target the parent dropdown-item containing the tag-select-wrapper
  .dropdown-item:has(.tag-select-wrapper),
  .dropdown-item:has(.tag-select-wrapper):active,
  .dropdown-item:has(.tag-select-wrapper):hover {
    background-color: transparent !important;
  }

  .activity-select-wrapper {
    padding: 12px;
    background-color: transparent !important;

    &:active,
    &:hover {
      background-color: transparent !important;
    }

    .activity-select-label {
      display: block;
      margin-bottom: 8px;
      font-weight: 500;
      font-size: 0.875rem;
      color: var(--bs-body-color);
      pointer-events: none;
      user-select: none;
      background-color: transparent !important;

      &:active,
      &:hover {
        background-color: transparent !important;
      }
    }

    .vs__control {
      padding: 0 4px;

      &:active,
      &:hover {
        background-color: transparent !important;
      }
    }
  }

  // Target the parent dropdown-item containing the activity-select-wrapper
  .dropdown-item:has(.activity-select-wrapper),
  .dropdown-item:has(.activity-select-wrapper):active,
  .dropdown-item:has(.activity-select-wrapper):hover {
    background-color: transparent !important;
  }

  .online-status-wrapper {
    padding: 12px;
    background-color: transparent !important;
    display: flex;
    align-items: center;
    gap: 12px;

    &:active,
    &:hover {
      background-color: transparent !important;
    }

    .online-status-label {
      font-weight: 500;
      font-size: 0.875rem;
      color: var(--bs-body-color);
      pointer-events: none;
      user-select: none;
      background-color: transparent !important;
      white-space: nowrap;
      margin-bottom: 0;

      &:active,
      &:hover {
        background-color: transparent !important;
      }
    }
  }

  // Target the parent dropdown-item containing the online-status-wrapper
  .dropdown-item:has(.online-status-wrapper),
  .dropdown-item:has(.online-status-wrapper):active,
  .dropdown-item:has(.online-status-wrapper):hover {
    background-color: transparent !important;
  }
}
</style>
